import type { Restaurant, MenuItem, Categoria, Theme, LandingContent } from "./types"

/**
 * Roulote Maria Bonita — Bombarral
 * Hamburgueria de rua (roulote). Identidade: sertão nordestino / cangaço,
 * exactamente a linguagem visual que a casa já usa no seu Instagram:
 * fundo âmbar, cartão de couro com contorno preto, cactos, chapéu de
 * cangaceiro com estrelas, bandeiras Brasil + Portugal.
 *
 * 🔴 REGRA DURA — ZERO INVENÇÃO.
 * Só entra aqui aquilo que está publicado pela própria casa (Instagram /
 * ficha Google). Os PREÇOS ainda não foram fornecidos pelo proprietário:
 * ficam a `0` e a UI mostra "a confirmar". NÃO preencher com estimativas.
 * Folha de recolha: F:/restaurant-software/docs/CARTA_MARIA_BONITA.md
 */

const PHOTO = "/maria-bonita/photos"
const LOGO = "/maria-bonita/logo.png"

/** Preço ainda não confirmado pela casa. */
const A_CONFIRMAR = 0

const theme: Theme = {
  bg: "#F0BD62", // sol do sertão — a cor real dos posts da casa
  bgWarm: "#E7AC4C",
  card: "#8C5125", // couro — o cartão castanho dos posts
  ink: "#241A12",
  inkMuted: "#6B4A2A", // 4.61:1 sobre o âmbar
  sepia: "#5C3D1F",
  primary: "#A82820", // vermelho MARIA (versão escura, legível)
  primaryDeep: "#7E1C16",
  accent: "#F2C230", // amarelo BONITA
  brass: "#C6362E", // vermelho de display (só texto grande)
  fire: "#2E9E4F", // verde "burguer" — só preenchimentos
  hairline: "rgba(36, 26, 18, 0.18)",
  fontDisplay: "var(--font-bonita-display)",
  fontBody: "var(--font-bonita-body)",
  fontMono: "var(--font-bonita-hand)",
  grain: true,
}

/**
 * Horário REAL — Ter a Sáb das 19h00 à meia-noite, Seg e Dom fechado.
 * Confirmado em duas fontes públicas independentes que espelham a ficha Google
 * (bellaciao.pt e restaurantguru.com, consultadas a 31-07-2026).
 * `abre`/`fecha` em minutos desde a meia-noite; fecha=1440 -> 00h00 do dia seguinte.
 */
export const mariaBonitaHorario = [
  { dia: 0, nome: "Domingo", fechado: true, abre: 0, fecha: 0 },
  { dia: 1, nome: "Segunda", fechado: true, abre: 0, fecha: 0 },
  { dia: 2, nome: "Terça", fechado: false, abre: 19 * 60, fecha: 24 * 60 },
  { dia: 3, nome: "Quarta", fechado: false, abre: 19 * 60, fecha: 24 * 60 },
  { dia: 4, nome: "Quinta", fechado: false, abre: 19 * 60, fecha: 24 * 60 },
  { dia: 5, nome: "Sexta", fechado: false, abre: 19 * 60, fecha: 24 * 60 },
  { dia: 6, nome: "Sábado", fechado: false, abre: 19 * 60, fecha: 24 * 60 },
]

/**
 * Localização.
 *
 * As coordenadas vêm da ficha pública da casa (espelhada em restaurantguru,
 * consultada a 31-07-2026) e foram confirmadas por geocodificação inversa no
 * OpenStreetMap: caem em Bombarral, na zona da Rua Dom Nuno Álvares Pereira.
 *
 * 🔴 A MORADA ESCRITA continua a ser só `2540-081 Bombarral` — é o que a casa
 * publica. O código postal do ponto (2540-086) não bate certo com o da ficha,
 * por isso NÃO escrevemos nome de rua até o proprietário confirmar. O mapa
 * mostra o ponto real; o texto não inventa a porta.
 *
 * Mapa em OpenStreetMap e não Google: o `output=embed` do Google passou a
 * responder `X-Frame-Options: SAMEORIGIN` (o iframe fica em branco) e a
 * alternativa oficial exige chave de API. O OSM não precisa de chave nem
 * mete cookies de terceiros.
 */
export const mariaBonitaMapa = {
  lat: 39.2707083,
  lon: -9.1558834,
  /** caixa de ~450 m à volta do ponto */
  bbox: "-9.1599,39.2687,-9.1519,39.2727",
  /** o botão "Como chegar" abre a ficha real no Google Maps */
  query: "Roulote Maria Bonita Bombarral",
}

const landing: LandingContent = {
  heroImg: `${PHOTO}/roulote.jpg`,
  heroPill: "Ter a Sáb · 19h00–00h00 · Bombarral",
  heroPillDotColor: "#2E9E4F",
  heroHeadlineBefore: "Sabor que faz a ",
  heroHeadlineHighlight: "diferença",
  heroHeadlineAfter: "!",
  heroSubtitle:
    "Hambúrgueres, cachorros, bifanas e durum feitos na roulote, à noite, em Bombarral. Do sertão para a estrada.",
  stepsTitle: "Como funciona",
  stepsLead: "Escolhes no telemóvel, nós fazemos na hora, levantas na janela.",
  steps: [
    {
      n: "01",
      iconName: "Smartphone",
      title: "Vês a ementa",
      body: "Aproxima o telemóvel do cartão da roulote e a ementa abre sozinha. Sem app, sem instalar nada.",
    },
    {
      n: "02",
      iconName: "Flame",
      title: "Fazemos na hora",
      body: "Cada pedido vai para a chapa no momento. Pão, carne e batata palha acabados de fazer.",
    },
    {
      n: "03",
      iconName: "ShoppingBag",
      title: "Levantas na janela",
      body: "Chamamos pelo número do pedido. Comes na esplanada ou levas para casa.",
    },
  ],
  galleryTitle: "O que sai da chapa",
  galleryLabel: "Bombarral · à noite, na roulote",
  gallery: [
    { src: `${PHOTO}/hamburguer.png`, label: "Hambúrguer", alt: "Hambúrguer com batata frita da Maria Bonita" },
    { src: `${PHOTO}/cachorro.png`, label: "Cachorro Especial", alt: "Cachorro especial com batata palha" },
    { src: `${PHOTO}/durum.png`, label: "Durum Kebab", alt: "Dois durum kebab enrolados" },
    { src: `${PHOTO}/bifana.png`, label: "Bifana no Pão", alt: "Bifana em pão caseiro num prato de barro" },
  ],
  picksLabel: "Da nossa janela",
  picksTitle: "O que já servimos",
  picks: [
    { nome: "Cachorros Especial", preco: "", popular: true },
    { nome: "Hambúrguer", preco: "" },
    { nome: "Durum Kebab", preco: "" },
    { nome: "Bifana no Pão", preco: "" },
  ],
  picksImage: `${PHOTO}/hamburguer.png`,
  picksImageAlt: "Hambúrguer da Maria Bonita",
  ctaEmenta: "Ver a ementa",
  footerQuote: "Sabor que faz a diferença!",
  footerCopyright: "© Roulote Maria Bonita · Bombarral",
}

const restaurant: Restaurant = {
  slug: "maria-bonita",
  nome: "Maria Bonita",
  subtitulo: "Burguer · Cachorros · Bifanas",
  morada: "2540-081 Bombarral",
  telefone: "+351 933 067 611",
  whatsapp: "+351933067611",
  horario: "Terça a sábado · 19h00 às 00h00 · Segunda e domingo fechado",
  imagemCapa: `${PHOTO}/roulote.jpg`,
  logoSrc: LOGO,
  nif: "",
  theme,
  landing,
  ementaTitulo: "A nossa ementa",
  ementaSubtitulo:
    "Feito na hora, na roulote. A carta completa está a ser afinada com a casa — por agora ficam aqui os clássicos.",
}

/**
 * Uma só categoria enquanto a carta completa não chega da casa.
 * Quando chegar: separar em Hambúrgueres / Cachorros / Bifanas / Kebabs /
 * Acompanhamentos / Bebidas — o componente de ementa mostra os separadores
 * automaticamente assim que existir mais do que uma categoria.
 */
const categorias: Categoria[] = [{ id: "carta", nome: "A carta", icone: "" }]

export const mariaBonitaMenu: MenuItem[] = [
  {
    id: "mb-cachorro-especial",
    nome: "Cachorros Especial",
    descricao: "Ovo, queijo, cenoura e batata palha.",
    preco: A_CONFIRMAR,
    imagem: `${PHOTO}/cachorro.png`,
    categoria: "carta",
    popular: true,
    notaInterna: "Descrição publicada pela casa no Instagram. Preço por confirmar.",
  },
  {
    id: "mb-hamburguer",
    nome: "Hambúrguer",
    descricao: "O clássico da casa, com batata frita.",
    preco: A_CONFIRMAR,
    imagem: `${PHOTO}/hamburguer.png`,
    categoria: "carta",
    popular: true,
    notaInterna: "A casa não publicou ingredientes. Confirmar variantes e preço.",
  },
  {
    id: "mb-durum-kebab",
    nome: "Durum Kebab",
    descricao: "Carne, queijo, cenoura e batata palha.",
    preco: A_CONFIRMAR,
    imagem: `${PHOTO}/durum.png`,
    categoria: "carta",
    notaInterna: "Descrição publicada pela casa no Instagram. Preço por confirmar.",
  },
  {
    id: "mb-bifana",
    nome: "Bifana no Pão",
    descricao: "Pão e carne. Simples como deve ser.",
    preco: A_CONFIRMAR,
    imagem: `${PHOTO}/bifana.png`,
    categoria: "carta",
    notaInterna: "Descrição publicada pela casa no Instagram. Preço por confirmar.",
  },
]

/** Factos verificáveis (ficha Google pública da casa). Nada estimado. */
export const mariaBonitaFactos = [
  { label: "No Google", valor: "4,5 ★", nota: "55 avaliações" },
  { label: "Esplanada", valor: "Sim", nota: "mesas à porta" },
  { label: "Take-away", valor: "Sim", nota: "leva para casa" },
  { label: "Cães", valor: "Bem-vindos", nota: "e cadeiras de bebé" },
]

export const mariaBonita: { restaurant: Restaurant; categorias: Categoria[]; menu: MenuItem[] } = {
  restaurant,
  categorias,
  menu: mariaBonitaMenu,
}
